<template>
  <div class="scene-table">
    <div class="scene-table-inner">
      <div class="scene-table-scroll">
        <table class="scene-table-main" :style="tableStyle">
          <thead>
            <tr>
              <th v-if="selectable" class="col-select" :class="{ 'radio-select': selectionType === 'radio' }"></th>
              <th
                v-for="column in columns"
                :key="column.key"
                :style="columnStyle(column)"
              >
                {{ column.title }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row[rowKey] || row.id">
              <td v-if="selectable" class="col-select" :class="{ 'radio-select': selectionType === 'radio' }">
                <input
                  :type="selectionInputType"
                  :name="selectionType === 'radio' ? tableName : undefined"
                  :checked="isSelected(row)"
                  :disabled="selectionDisabled"
                  @change="toggleRow(row, $event)"
                />
              </td>
              <td
                v-for="column in columns"
                :key="column.key"
                :style="[columnStyle(column), cellStyle(row, column)]"
              >
              <template v-if="column.type === 'image'">
                  <img v-if="row[column.key]" class="table-image" :src="row[column.key]" draggable="false" />
                  <span v-else class="table-image-placeholder"></span>
                </template>
                <template v-else-if="column.type === 'badge'">
                  <span class="table-type-badge" :class="badgeClass(row, column)">{{ formatCell(row, column) }}</span>
                </template>
                <template v-else-if="column.type === 'input'">
                  <input
                    class="table-cell-input"
                    :value="row[column.key]"
                    :disabled="isCellInputDisabled(row, column)"
                    @input="changeCell(row, column, $event)"
                  />
                </template>
                <template v-else-if="column.type === 'select'">
                  <select
                    class="table-cell-input"
                    :value="row[column.key]"
                    :disabled="isCellInputDisabled(row, column)"
                    @change="changeCell(row, column, $event)"
                  >
                    <option value="">请选择</option>
                    <option v-for="option in column.options || []" :key="option.value || option" :value="option.value || option">
                      {{ option.label || option }}
                    </option>
                  </select>
                </template>
                <template v-else>
                  {{ formatCell(row, column) }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="showPagination" class="scene-table-pagination">
        <button class="page-btn" :disabled="page <= 1" @click="changePage(page - 1)">‹</button>
        <span class="page-info">{{ page }}</span>
        <button class="page-btn" :disabled="page >= totalPages" @click="changePage(page + 1)">›</button>
        <span class="page-total">共 {{ total }} 条</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SceneTable',
  props: {
    config: { type: Object, required: true }
  },
  data() {
    return {
      page: 1
    }
  },
  computed: {
    columns() {
      return Array.isArray(this.config.columns) ? this.config.columns : []
    },
    rows() {
      return Array.isArray(this.config.rows) ? this.config.rows : []
    },
    rowKey() {
      return this.config.rowKey || 'id'
    },
    selectable() {
      return this.config.selectable === true
    },
    selectionType() {
      return this.config.selectionType === 'radio' ? 'radio' : 'checkbox'
    },
    selectionInputType() {
      return this.selectionType
    },
    tableName() {
      return 'scene-table-' + (this.config.id || this._uid)
    },
    selectedKeys() {
      return Array.isArray(this.config.selectedKeys) ? this.config.selectedKeys : []
    },
    selectionDisabled() {
      return this.config.selectionDisabled === true
    },
    pageSize() {
      return Number(this.config.pageSize) > 0 ? Number(this.config.pageSize) : 0
    },
    total() {
      return Number(this.config.total) > 0 ? Number(this.config.total) : this.rows.length
    },
    totalPages() {
      if (!this.pageSize) return 1
      return Math.max(1, Math.ceil(this.rows.length / this.pageSize))
    },
    showPagination() {
      return !!this.pageSize
    },
    tableStyle() {
      const contentWidth = this.columns.reduce((total, column) => {
        const width = Number(column.width)
        return total + (Number.isFinite(width) ? width : 120)
      }, this.selectable ? 40 : 0)
      const style = { minWidth: Math.max(contentWidth, 100) + 'px' }
      const fontSize = Number(this.config.fontSize)
      if (Number.isFinite(fontSize) && fontSize > 0) style.fontSize = fontSize + 'px'
      return style
    },
    pagedRows() {
      if (!this.pageSize) return this.rows
      const start = (this.page - 1) * this.pageSize
      return this.rows.slice(start, start + this.pageSize)
    }
  },
  watch: {
    config: {
      deep: true,
      handler() {
        if (this.page > this.totalPages) this.page = this.totalPages
      }
    }
  },
  methods: {
    columnStyle(column) {
      const style = {}
      if (column.width) style.width = typeof column.width === 'number' ? column.width + 'px' : column.width
      if (column.align) style.textAlign = column.align
      if (column.whiteSpace) style.whiteSpace = column.whiteSpace
      return style
    },
    cellStyle(row, column) {
      const style = {}
      const rowHeight = Number(this.config.rowHeight)
      if (Number.isFinite(rowHeight) && rowHeight > 0) style.height = rowHeight + 'px'
      const columnStyle = typeof column.cellStyle === 'function' ? column.cellStyle(row) : column.cellStyle
      return { ...style, ...(columnStyle || {}) }
    },
    isCellInputDisabled(row, column) {
      if (typeof column.inputDisabled === 'function') return column.inputDisabled(row) === true
      return column.inputDisabled === true
    },
    formatCell(row, column) {
      if (typeof column.formatter === 'function') return column.formatter(row)
      return row[column.key]
    },
    badgeClass(row, column) {
      const value = String(this.formatCell(row, column) || '')
      if (value === '换') return 'badge-exchange'
      if (value === '退') return 'badge-return'
      if (value === '补') return 'badge-resend'
      return ''
    },
    isSelected(row) {
      const key = row[this.rowKey]
      return this.selectedKeys.indexOf(key) > -1
    },
    toggleRow(row, event) {
      if (this.selectionDisabled) return
      const checked = !!(event && event.target && event.target.checked)
      this.$emit('selection-change', { row, checked, page: this.page })
    },
    changeCell(row, column, event) {
      const value = event && event.target ? event.target.value : ''
      this.$emit('cell-change', {
        row,
        key: column.key,
        value,
        page: this.page
      })
    },
    changePage(nextPage) {
      const page = Math.max(1, Math.min(this.totalPages, nextPage))
      this.page = page
      this.$emit('page-change', page)
    }
  }
}
</script>

<style scoped>
.scene-table {
  position: absolute;
  box-sizing: border-box;
  overflow: hidden;
}

.scene-table-inner {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #fff;
  border: 1px solid #ebeef5;
}

.scene-table-scroll {
  flex: 1 1 auto;
  overflow: auto;
}

.scene-table-main {
  min-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
  color: #303133;
}

.scene-table-main th,
.scene-table-main td {
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid #ebeef5;
  vertical-align: middle;
  background: #fff;
}

.scene-table-main th {
  font-weight: 600;
  text-align: left;
  white-space: nowrap;
  background: #f8fafc;
}

.col-select {
  width: 40px;
  text-align: center !important;
}

.col-select.radio-select input[type="radio"] {
  appearance: radio;
  -webkit-appearance: radio;
  width: auto;
  height: auto;
  cursor: pointer;
}

.col-select input[type="checkbox"] {
  cursor: pointer;
}

.table-image-placeholder {
  display: inline-block;
  width: 24px;
  height: 24px;
  background: #f1f3f6;
  border: 1px solid #dcdfe6;
  border-radius: 2px;
}

.table-image {
  display: block;
  width: 48px;
  height: 48px;
  margin: 0 auto;
  object-fit: contain;
  background: #fff;
}

.table-type-badge {
  display: inline-block;
  min-width: 18px;
  font-weight: 700;
  text-align: center;
}

.badge-exchange { color: #159de0; }
.badge-return { color: #f04444; }
.badge-resend { color: #d6ae00; }

.table-cell-input {
  width: 100%;
  height: 28px;
  box-sizing: border-box;
  padding: 0 8px;
  border: 1px solid #dcdfe6;
  border-radius: 2px;
  outline: none;
  font-size: 12px;
}

.scene-table-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid #ebeef5;
  background: #fff;
  font-size: 12px;
  color: #606266;
}

.page-btn {
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  color: #409eff;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 2px;
  cursor: pointer;
}

.page-btn[disabled] {
  color: #c0c4cc;
  cursor: not-allowed;
}

.page-info {
  min-width: 20px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// ./src/components/simulate/SceneTable.vue